import { fetchApi, type GenericRes } from '@hunk/shared/api/fetchApi';
import type { OrderView } from '@hunk/shared/api/orders';
import type { ShopDeliverySettings } from '@hunk/shared/api/shop';
import type { OrderStatusType, PaymentOptionType, PaymentServiceType, TransactionStatusType } from '@hunk/shared/common';

/** Admin screens (ADMIN_TELEGRAM_IDS / ADMIN_EMAILS). Under /api/admin. Money in agorot. */
const A = '/api/admin';
type H = { headers?: HeadersInit };

//#region stats
export interface AdminStats {
  users: number;
  products: number;
  activeProducts: number;
  /** paid orders waiting to be handled (paid + processing) */
  toHandle: number;
  pendingPayment: number;
  /** paid orders' totals, last 30 days (agorot) */
  revenue30d: number;
  orders30d: number;
}
export interface AdminStatsRes extends GenericRes {
  stats?: AdminStats;
}
export const getStats = ({ headers }: H) => fetchApi<{}, AdminStatsRes>({ apiPath: `${A}/stats`, headers });
//#endregion

//#region products
export interface AdminProductImage {
  id: number;
  url: string;
}
export interface AdminProduct {
  id: number;
  name: string;
  description: string;
  sku: string | null;
  price: number;
  salePrice: number | null;
  stockQty: number | null;
  inStock: boolean;
  isActive: boolean;
  sortOrder: number;
  categoryIds: number[];
  images: AdminProductImage[];
  /** units sold in paid orders */
  sold: number;
  createdOn: string;
  updatedOn: string;
}
export interface AdminProductsRes extends GenericRes {
  products?: AdminProduct[];
}
export interface AdminProductRes extends GenericRes {
  product?: AdminProduct;
}
export interface SaveProductReq {
  name?: string;
  description?: string;
  sku?: string | null;
  price?: number;
  salePrice?: number | null;
  stockQty?: number | null;
  inStock?: boolean;
  isActive?: boolean;
  sortOrder?: number;
  categoryIds?: number[];
}
export const listProducts = ({ headers }: H) => fetchApi<{}, AdminProductsRes>({ apiPath: `${A}/products`, headers });
export const createProduct = (data: SaveProductReq, { headers }: H) => fetchApi<SaveProductReq, AdminProductRes>({ apiPath: `${A}/products`, method: 'POST', data, headers });
export const updateProduct = (id: number, data: SaveProductReq, { headers }: H) =>
  fetchApi<SaveProductReq, AdminProductRes>({ apiPath: `${A}/products/${id}`, method: 'PATCH', data, headers });
export const deleteProduct = (id: number, { headers }: H) => fetchApi<{}, GenericRes>({ apiPath: `${A}/products/${id}`, method: 'DELETE', headers });
/** image order: ids in the new order (first = main image) */
export const orderProductImages = (id: number, imageIds: number[], { headers }: H) =>
  fetchApi<{ imageIds: number[] }, AdminProductRes>({ apiPath: `${A}/products/${id}/images/order`, method: 'PUT', data: { imageIds }, headers });
export const deleteProductImage = (id: number, imageId: number, { headers }: H) =>
  fetchApi<{}, AdminProductRes>({ apiPath: `${A}/products/${id}/images/${imageId}`, method: 'DELETE', headers });
/** upload: POST the raw file (image/jpeg, image/png, image/webp; up to 10 MB) - not JSON, so not through fetchApi */
export const productImageUploadUrl = (id: number) => `${A}/products/${id}/images`;
//#endregion

//#region categories
export interface AdminCategory {
  id: number;
  name: string;
  slug: string;
  isHidden: boolean;
  sortOrder: number;
  /** products in it */
  products: number;
}
export interface AdminCategoriesRes extends GenericRes {
  categories?: AdminCategory[];
}
export interface SaveCategoryReq {
  name?: string;
  slug?: string;
  isHidden?: boolean;
}
export const listCategories = ({ headers }: H) => fetchApi<{}, AdminCategoriesRes>({ apiPath: `${A}/categories`, headers });
export const createCategory = (data: SaveCategoryReq, { headers }: H) =>
  fetchApi<SaveCategoryReq, AdminCategoriesRes>({ apiPath: `${A}/categories`, method: 'POST', data, headers });
export const updateCategory = (id: number, data: SaveCategoryReq, { headers }: H) =>
  fetchApi<SaveCategoryReq, AdminCategoriesRes>({ apiPath: `${A}/categories/${id}`, method: 'PATCH', data, headers });
export const deleteCategory = (id: number, { headers }: H) => fetchApi<{}, AdminCategoriesRes>({ apiPath: `${A}/categories/${id}`, method: 'DELETE', headers });
export const orderCategories = (ids: number[], { headers }: H) =>
  fetchApi<{ ids: number[] }, AdminCategoriesRes>({ apiPath: `${A}/categories/order`, method: 'PUT', data: { ids }, headers });
//#endregion

//#region orders
export interface AdminUserRef {
  id: number;
  tgUserId: number | null;
  tgUsername: string | null;
  email: string | null;
}
export interface AdminOrder extends OrderView {
  user: AdminUserRef | null;
  adminNote: string | null;
  paymentOption: PaymentOptionType | null;
  updatedOn: string;
}
export interface AdminOrdersRes extends GenericRes {
  orders?: AdminOrder[];
  total?: number;
}
export interface AdminOrderRes extends GenericRes {
  order?: AdminOrder;
  transactions?: AdminTransaction[];
}
export type OrderFilter = 'open' | 'pending_payment' | 'done' | 'canceled' | 'all';
export interface UpdateOrderReq {
  status?: OrderStatusType;
  trackingNumber?: string | null;
  adminNote?: string | null;
  /** send the customer a Telegram message about the new status (when they have Telegram) */
  notify?: boolean;
}
export const listOrders = (q: { filter: OrderFilter; search?: string; offset?: number }, { headers }: H) =>
  fetchApi<typeof q, AdminOrdersRes>({ apiPath: `${A}/orders`, data: q, headers });
export const getOrder = (id: number, { headers }: H) => fetchApi<{}, AdminOrderRes>({ apiPath: `${A}/orders/${id}`, headers });
export const updateOrder = (id: number, data: UpdateOrderReq, { headers }: H) =>
  fetchApi<UpdateOrderReq, AdminOrderRes>({ apiPath: `${A}/orders/${id}`, method: 'PATCH', data, headers });
//#endregion

//#region users
export interface AdminUser extends AdminUserRef {
  fullName: string | null;
  phone: string | null;
  createdOn: string;
  orders: number;
  /** paid orders total (agorot) */
  spent: number;
  isAdmin: boolean;
}
export interface AdminUsersRes extends GenericRes {
  users?: AdminUser[];
  total?: number;
}
export interface AdminUserRes extends GenericRes {
  user?: AdminUser;
  orders?: AdminOrder[];
}
export const listUsers = (q: { search?: string; offset?: number }, { headers }: H) => fetchApi<typeof q, AdminUsersRes>({ apiPath: `${A}/users`, data: q, headers });
export const getUser = (id: number, { headers }: H) => fetchApi<{}, AdminUserRes>({ apiPath: `${A}/users/${id}`, headers });
//#endregion

//#region transactions
export interface AdminTransaction {
  id: number;
  internalTransactionId: string;
  createdOn: string;
  updatedOn: string;
  status: TransactionStatusType;
  paymentService: PaymentServiceType;
  paymentOption: PaymentOptionType | null;
  paymentServiceTransactionId: string | null;
  chargedAmount: number;
  last4Digits: string | null;
  orderId: number | null;
  user: AdminUserRef | null;
}
export interface AdminTransactionsRes extends GenericRes {
  transactions?: AdminTransaction[];
  total?: number;
}
export const listTransactions = (q: { status?: TransactionStatusType | 'all'; search?: string; offset?: number }, { headers }: H) =>
  fetchApi<typeof q, AdminTransactionsRes>({ apiPath: `${A}/transactions`, data: q, headers });
//#endregion

//#region shop settings
export interface ShopSettings extends ShopDeliverySettings {
  /** Telegram ids that get a message for every paid order (besides ADMIN_TELEGRAM_IDS) */
  notifyTelegramIds: number[];
}
export interface ShopSettingsRes extends GenericRes {
  settings?: ShopSettings;
}
export const getShopSettings = ({ headers }: H) => fetchApi<{}, ShopSettingsRes>({ apiPath: `${A}/shopSettings`, headers });
export const saveShopSettings = (data: ShopSettings, { headers }: H) => fetchApi<ShopSettings, ShopSettingsRes>({ apiPath: `${A}/shopSettings`, method: 'PUT', data, headers });
//#endregion
